import { useId } from "react";

import { cn } from "../lib/cn";

export interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type"
> {
  label?: string;
  ref?: React.Ref<HTMLInputElement>;
}

/** A restyled native checkbox; semantics and keyboard behaviour are untouched. */
export function Checkbox({
  label,
  id,
  className,
  disabled,
  ref,
  ...rest
}: CheckboxProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <label
      htmlFor={inputId}
      className={cn(
        "inline-flex items-start gap-2 font-body text-sm",
        disabled
          ? "cursor-not-allowed text-faint"
          : "cursor-pointer text-ink-body",
      )}
    >
      <span className="relative mt-px inline-grid size-4 shrink-0 place-items-center">
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          disabled={disabled}
          className={cn(
            "peer size-4 appearance-none rounded-[2px] border bg-card",
            "transition duration-150 ease-paper",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
            "checked:bg-ink",
            disabled
              ? "cursor-not-allowed border-rule bg-recess checked:bg-hollow"
              : "cursor-pointer border-ink",
            className,
          )}
          {...rest}
        />
        <svg
          aria-hidden="true"
          viewBox="0 0 16 16"
          fill="none"
          className="pointer-events-none absolute hidden size-3.5 text-paper peer-checked:block"
        >
          <path
            d="M3.2 8.4 L6.3 11.6 L12.9 3.9"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      {label && <span>{label}</span>}
    </label>
  );
}
